import { AgeDiscrete, AgeRange, AgeSchoolLevel, AgeGroup } from "src/state/modules/team";

/**
 * MLB Pitch Smart pitching guidelines.
 * Source: https://www.mlb.com/pitch-smart/pitching-guidelines
 *
 * Each age bracket encodes TWO rules:
 *  - `dailyMax`: the most pitches allowed in a single outing.
 *  - `tiers`: required days of rest after an outing, by pitch count.
 *
 * Tiers are ordered ascending. Tier index === required rest days, so the
 * lower bound of tier `r` is `tiers[r-1].upTo + 1` (or 1 for r === 0).
 * The top tier uses `upTo: Infinity` and renders as "{lower}+".
 */
export interface RestTier {
  /** inclusive upper bound of pitches for this tier (Infinity for the top tier) */
  upTo: number;
  /** required days of rest after an outing that lands in this tier */
  rest: number;
}

export interface PitchSmartBracket {
  /** stable id, also the display label e.g. "13-14" */
  id: string;
  dailyMax: number;
  tiers: RestTier[];
}

// Younger brackets share the 20/35/50/65 thresholds; older ones shift to 30/45/60.
export const PITCH_SMART_BRACKETS: PitchSmartBracket[] = [
  { id: "7-8", dailyMax: 50, tiers: [{ upTo: 20, rest: 0 }, { upTo: 35, rest: 1 }, { upTo: 50, rest: 2 }] },
  {
    id: "9-10",
    dailyMax: 75,
    tiers: [
      { upTo: 20, rest: 0 },
      { upTo: 35, rest: 1 },
      { upTo: 50, rest: 2 },
      { upTo: 65, rest: 3 },
      { upTo: Infinity, rest: 4 },
    ],
  },
  {
    id: "11-12",
    dailyMax: 85,
    tiers: [
      { upTo: 20, rest: 0 },
      { upTo: 35, rest: 1 },
      { upTo: 50, rest: 2 },
      { upTo: 65, rest: 3 },
      { upTo: Infinity, rest: 4 },
    ],
  },
  {
    id: "13-14",
    dailyMax: 95,
    tiers: [
      { upTo: 20, rest: 0 },
      { upTo: 35, rest: 1 },
      { upTo: 50, rest: 2 },
      { upTo: 65, rest: 3 },
      { upTo: Infinity, rest: 4 },
    ],
  },
  {
    id: "15-16",
    dailyMax: 95,
    tiers: [
      { upTo: 30, rest: 0 },
      { upTo: 45, rest: 1 },
      { upTo: 60, rest: 2 },
      { upTo: 75, rest: 3 },
      { upTo: Infinity, rest: 4 },
    ],
  },
  {
    id: "17-18",
    dailyMax: 105,
    tiers: [
      { upTo: 30, rest: 0 },
      { upTo: 45, rest: 1 },
      { upTo: 60, rest: 2 },
      { upTo: 80, rest: 3 },
      { upTo: Infinity, rest: 4 },
    ],
  },
  {
    id: "19-22",
    dailyMax: 120,
    tiers: [
      { upTo: 30, rest: 0 },
      { upTo: 45, rest: 1 },
      { upTo: 60, rest: 2 },
      { upTo: 80, rest: 3 },
      { upTo: 105, rest: 4 },
      { upTo: Infinity, rest: 5 },
    ],
  },
];

/** Highest required-rest tier across all brackets, for table column count (0..5). */
export const MAX_REST_DAYS = 5;

export const getBracket = (id: string): PitchSmartBracket =>
  PITCH_SMART_BRACKETS.find((b) => b.id === id) ?? PITCH_SMART_BRACKETS[3]; // default 13-14

/** Friendly label for the age dropdown, e.g. "Ages 13–14". */
export const bracketLabel = (bracket: PitchSmartBracket): string => `Ages ${bracket.id.replace("-", "–")}`;

/** Required days of rest after an outing of `pitches` in the given bracket. */
export function requiredRest(pitches: number, bracket: PitchSmartBracket): number {
  if (!pitches || pitches <= 0) return 0;
  for (const tier of bracket.tiers) {
    if (pitches <= tier.upTo) return tier.rest;
  }
  return bracket.tiers[bracket.tiers.length - 1].rest;
}

/** The pitch-count range label for a given rest column, e.g. "21-35", "66+", or "N/A". */
export function rangeLabelForRest(bracket: PitchSmartBracket, restDays: number): string {
  const tier = bracket.tiers[restDays];
  if (!tier || tier.rest !== restDays) return "N/A";
  const lower = restDays === 0 ? 1 : bracket.tiers[restDays - 1].upTo + 1;
  return tier.upTo === Infinity ? `${lower}+` : `${lower}-${tier.upTo}`;
}

export type AvailabilityKind = "available" | "resting" | "threw_today";

export interface PitcherAvailability {
  kind: AvailabilityKind;
  /** days of rest still owed before the pitcher is available again */
  restOwed: number;
  /** pitches thrown today (0 if none) */
  threwToday: number;
  /** rest days the pitcher will owe because of today's outing */
  restAfterToday: number;
  /** pitches remaining before the daily max (max if fully available) */
  remaining: number;
  /** plain-language summary for display */
  label: string;
}

/** One pitcher's row of display data, shared by the desktop grid and the mobile cards. */
export type PitchSmartTableData = {
  /** player id, used to link the name to the player profile (desktop grid) */
  id: string;
  name: string;
  status: string;
  availability: PitcherAvailability;
  IP: number;
  "#P": number;
  "S%": number;
  "SO%": number;
  WHIP: number;
  BAA: number;
  bf: number;
  so: number;
  TODAY: number;
  DAY1: number;
  DAY2: number;
  DAY3: number;
  DAY4: number;
  DAY5: number;
};

/**
 * Compute a pitcher's availability TODAY from their recent outings.
 *
 * `counts[d]` = pitches thrown `d` days ago (index 0 === today).
 *
 * An outing `d` days ago that requires `R` rest days blocks today when `d <= R`;
 * the pitcher is available again `R + 1 - d` days from now. The most restrictive
 * prior outing wins.
 */
export function computeAvailability(counts: number[], bracket: PitchSmartBracket): PitcherAvailability {
  let restOwed = 0;
  for (let d = 1; d < counts.length; d++) {
    const p = counts[d] ?? 0;
    if (p > 0) {
      restOwed = Math.max(restOwed, requiredRest(p, bracket) + 1 - d);
    }
  }
  restOwed = Math.max(0, restOwed);

  const threwToday = counts[0] ?? 0;
  if (threwToday > 0) {
    const restAfterToday = requiredRest(threwToday, bracket);
    return {
      kind: "threw_today",
      restOwed,
      threwToday,
      restAfterToday,
      remaining: Math.max(0, bracket.dailyMax - threwToday),
      label: `Threw ${threwToday} today${restAfterToday > 0 ? ` · owes ${restAfterToday}d` : ""}`,
    };
  }

  if (restOwed > 0) {
    return {
      kind: "resting",
      restOwed,
      threwToday: 0,
      restAfterToday: 0,
      remaining: 0,
      label: `Rest ${restOwed} more day${restOwed === 1 ? "" : "s"}`,
    };
  }

  return {
    kind: "available",
    restOwed: 0,
    threwToday: 0,
    restAfterToday: 0,
    remaining: bracket.dailyMax,
    label: `Available · ${bracket.dailyMax} max`,
  };
}

/**
 * Sort key for "least available → most available". Sorting ascending surfaces
 * the pitchers a coach can't use first:
 *   resting (most days owed first) → threw today (most rest owed after first) → available.
 * Callers break ties with a secondary key (innings pitched, descending).
 */
export function availabilityRank(avail: PitcherAvailability): number {
  switch (avail.kind) {
    case "resting":
      return -100 - avail.restOwed; // can't pitch today and owes the most rest
    case "threw_today":
      return -avail.restAfterToday; // already used today; more rest owed after = less available
    case "available":
    default:
      return 1; // ready now — most available
  }
}

/** Soft backgrounds keyed to the required rest an outing/status triggers. */
export const REST_COLORS: Record<number, string> = {
  0: "#e0edde", // green  — same/next day
  1: "#f1f1da", // yellow — 1 day
  2: "#f7ecd9", // amber  — 2 days
  3: "#f6ddc8", // orange — 3 days
  4: "#f3cccc", // red    — 4 days
  5: "#ecbcbc", // deep red — 5 days
};

export const STATUS_COLORS: Record<AvailabilityKind, string> = {
  available: "#e0edde",
  threw_today: "#dbe6f2", // blue — done for the day, not a danger
  resting: "#f3cccc",
};

/** Background for a day cell, by the rest its pitch count triggers (empty for 0). */
export function dayCellColor(pitches: number, bracket: PitchSmartBracket): string {
  if (!pitches || pitches <= 0) return "";
  return REST_COLORS[requiredRest(pitches, bracket)] ?? "";
}

/** Background for the Status cell. Resting escalates with days owed. */
export function statusColor(avail: PitcherAvailability): string {
  if (avail.kind === "resting") return REST_COLORS[Math.min(avail.restOwed, MAX_REST_DAYS)] ?? STATUS_COLORS.resting;
  return STATUS_COLORS[avail.kind];
}

/**
 * Best-guess Pitch Smart bracket for a team's age group. A coach can override
 * this in the UI — youth teams rostered by age group span real ages, and the
 * player records carry no birthdate.
 */
export function ageGroupToBracketId(ageGroup?: AgeGroup): string {
  switch (ageGroup) {
    case AgeDiscrete.EIGHT_AND_UNDER:
      return "7-8";
    case AgeDiscrete.NINE_AND_UNDER:
    case AgeDiscrete.TEN_AND_UNDER:
      return "9-10";
    case AgeDiscrete.ELEVEN_AND_UNDER:
    case AgeDiscrete.TWELVE_AND_UNDER:
    case AgeRange.UNDER_THIRTEEN:
      return "11-12";
    case AgeDiscrete.THIRTEEN_AND_UNDER:
    case AgeDiscrete.FOURTEEN_AND_UNDER:
    case AgeRange.THIRTEEN_TO_EIGHTEEN:
      return "13-14";
    case AgeDiscrete.FIFTEEN_AND_UNDER:
    case AgeDiscrete.SIXTEEN_AND_UNDER:
      return "15-16";
    case AgeDiscrete.SEVENTEEN_AND_UNDER:
    case AgeDiscrete.EIGHTEEN_AND_UNDER:
      return "17-18";
    case AgeDiscrete.EIGHTEEN_AND_OVER:
    case AgeRange.OVER_EIGHTEEN:
      return "19-22";
    case AgeSchoolLevel.ELEMENTARY:
      return "9-10";
    case AgeSchoolLevel.MIDDLE_TWELVE_AND_UNDER:
      return "11-12";
    case AgeSchoolLevel.MIDDLE_THIRTEEN_AND_OVER:
      return "13-14";
    case AgeSchoolLevel.HIGH_FRESHMAN:
    case AgeSchoolLevel.HIGH_JUNIOR_VARSITY:
      return "15-16";
    case AgeSchoolLevel.HIGH_VARSITY:
      return "17-18";
    case AgeSchoolLevel.COLLEGE:
      return "19-22";
    default:
      return "13-14";
  }
}
